import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '../utils/api';
import NumberInput from '../components/NumberInput';

function PasswordVisibilityIcon({ visible }) {
  return visible ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 3L21 21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10.58 10.58A2 2 0 0 0 13.41 13.41" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9.88 4.24A10.94 10.94 0 0 1 12 4c5 0 9.27 3.11 11 7.5a11.8 11.8 0 0 1-2.16 3.19" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6.61 6.61A11.86 11.86 0 0 0 1 11.5C2.73 15.89 7 19 12 19a10.9 10.9 0 0 0 4.23-.84" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M1 12C2.73 7.61 7 4.5 12 4.5C17 4.5 21.27 7.61 23 12C21.27 16.39 17 19.5 12 19.5C7 19.5 2.73 16.39 1 12Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

export default function Signup() {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [loginMethod, setLoginMethod] = useState('email'); // 'email' or 'phone'
  const [otpRequested, setOtpRequested] = useState(false);

  const [age, setAge] = useState('');
  const [gender, setGender] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const { register, verifyOtp } = useAuth();
  const { setTheme } = useTheme();
  const navigate = useNavigate();
  const { requestOtp } = api;

  useEffect(() => {
    setTheme(gender === 'female' ? 'female' : null);
  }, [gender, setTheme]);

  const isFemale = gender === 'female';
  const accent = isFemale ? '#ff00aa' : '#0064b2';
  const accentHov = isFemale ? '#ff4dc4' : '#3399e6';
  const accentRgba = isFemale ? 'rgba(255,0,170,0.1)' : 'rgba(0,100,178,0.1)';
  const accentBorderStrong = isFemale ? 'rgba(255, 0, 170, 0.2)' : 'rgba(0,100,178,0.2)';
  const accentBorder = accentBorderStrong;
  const gradientStyle = isFemale
    ? 'linear-gradient(90deg, #990066, #ff00aa, #ff4dc4)'
    : 'linear-gradient(90deg, #004d8c, #0064b2, #3399e6)';
  const cardBackground = '#000000';
  const pageBackground = '#000000';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!gender) { setError(t('auth.pleaseSelectGender')); return; }
    if (!age || Number(age) < 10 || Number(age) > 120) { setError(t('auth.validAge')); return; }

    setLoading(true);
    try {
      if (loginMethod === 'email') {
        if (password !== confirmPassword) { setError(t('auth.passwordsDontMatch')); setLoading(false); return; }
        await register(email, password, name, gender, Number(age));
        setSuccess(true);
      } else {
        await requestOtp(phoneNumber);
        setOtpRequested(true);
      }
    } catch (err) {
      setError(err.message || t('auth.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: pageBackground, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem 1.5rem', fontFamily: "'Roboto', sans-serif", position: 'relative', overflow: 'hidden' }}>

      {/* Background watermark */}
      <div style={{ position: 'absolute', bottom: '5%', left: '50%', transform: 'translateX(-50%)', fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(4rem, 15vw, 10rem)', letterSpacing: '0.1em', color: isFemale ? 'rgba(255,0,170,0.06)' : 'rgba(27,213,173,0.04)', whiteSpace: 'nowrap', pointerEvents: 'none', userSelect: 'none' }}>
        YALLA SALON
      </div>

      {/* Radial glow */}
      <div style={{ position: 'absolute', top: '30%', left: '50%', transform: 'translate(-50%, -50%)', width: '600px', height: '600px', borderRadius: '50%', background: isFemale ? 'radial-gradient(circle, rgba(255,0,170,0.08) 0%, transparent 70%)' : 'radial-gradient(circle, rgba(27,213,173,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <div style={{ width: '100%', maxWidth: '480px', position: 'relative', zIndex: 1 }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Link to="/" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', marginBottom: '1.25rem' }}>
            <img
              src="/Logo-Yalla-Salon.png"
              alt="Yalla Salon"
              style={{ height: '70px', width: 'auto', mixBlendMode: 'screen' }}
            />
          </Link>
          <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '2.5rem', letterSpacing: '0.08em', color: '#ffffff', marginBottom: '0.4rem' }}>{t('auth.createAccount')}</h1>
          <div style={{ width: '40px', height: '3px', background: accent, borderRadius: '2px', margin: '0 auto 0.75rem' }} />
          <p style={{ fontSize: '0.88rem', color: '#ffffff' }}>{t('auth.joinYalla')}</p>
        </div>

        {/* Card */}
        <div style={{ background: cardBackground, border: `1px solid ${accentBorderStrong}`, borderRadius: '12px', padding: '2.5rem 2rem', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: gradientStyle }} />

          {error && (
            <div style={{ marginBottom: '1.25rem', padding: '0.85rem 1rem', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', color: '#fca5a5', fontSize: '0.85rem' }}>
              {error}
            </div>
          )}

          {success ? (
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(34,197,94,0.1)', border: '2px solid #22c55e', color: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', margin: '0 auto 1.5rem' }}>
                ✓
              </div>
              <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '2rem', color: '#ffffff', marginBottom: '1rem' }}>{t('auth.almostThere')}</h2>
              <p style={{ color: '#ffffff', marginBottom: '2rem', lineHeight: 1.6 }}>
                {t('auth.verificationEmailSent', { email })}
              </p>
              <Link to="/login" className="btn-orange" style={{ display: 'inline-flex', justifyContent: 'center', width: '100%', padding: '0.9rem' }}>
                {t('auth.goToLogin')}
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Gender toggle */}
              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ fontFamily: "'Oswald', sans-serif", fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#ffffff', marginBottom: '0.75rem' }}>{t('auth.iAmA')}</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  {[['male', '/images/male-icon.png', t('auth.male')], ['female', '/images/female-icon.png', t('auth.female')]].map(([g, iconSrc, label]) => {
                    const selected = gender === g;
                    const selAccent = g === 'female' ? '#ff00aa' : '#0064b2';
                    const selBorder = 'rgba(255,255,255,0.25)';
                    return (
                      <button
                        key={g}
                        type="button"
                        onClick={() => setGender(g)}
                        style={{
                          padding: '0.85rem',
                          border: `2px solid ${selected ? selAccent : selBorder}`,
                          borderRadius: '6px',
                          background: selected ? selAccent : 'transparent',
                          color: '#ffffff',
                          fontFamily: "'Oswald', sans-serif",
                          fontWeight: 700,
                          fontSize: '0.9rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.4rem',
                        }}
                      >
                        <img src={iconSrc} alt="" style={{ width: 28, height: 28, objectFit: 'contain' }} />
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Shared Fields */}
              <div style={{ marginBottom: '1.1rem' }}>
                <label style={{ display: 'block', fontFamily: "'Oswald', sans-serif", fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#ffffff', marginBottom: '0.4rem' }}>{t('auth.fullName')}</label>
                <input
                  type="text"
                  placeholder={t('auth.placeholderFullName')}
                  value={name}
                  onChange={e => setName(e.target.value)}
                  required
                  className="barber-input login-input"
                  style={{ color: '#ffffff' }}
                />
              </div>
              <div style={{ marginBottom: '1.1rem' }}>
                <label style={{ display: 'block', fontFamily: "'Oswald', sans-serif", fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#ffffff', marginBottom: '0.4rem' }}>{t('auth.age')}</label>
                <NumberInput
                  placeholder={t('auth.placeholderAge')}
                  value={age}
                  onChange={setAge}
                  min={10}
                  max={120}
                  required
                  inputClassName="barber-input login-input"
                  inputStyle={{ color: '#ffffff' }}
                />
              </div>

              {/* Email: Email, Password, Confirm Password | Phone: Phone, Verification Code */}
              {loginMethod === 'email' ? (
                <>
                  {[
                    { labelKey: 'auth.email', type: 'email', value: email, setter: setEmail, placeholderKey: 'auth.placeholderEmail' },
                    { labelKey: 'auth.password', type: 'password', value: password, setter: setPassword, placeholderKey: 'auth.placeholderPassword', isPassword: true },
                    { labelKey: 'auth.confirmPassword', type: 'password', value: confirmPassword, setter: setConfirmPassword, placeholderKey: 'auth.placeholderPassword', isPassword: true },
                  ].map(({ labelKey, type, value, setter, placeholderKey, isPassword }) => (
                    <div key={labelKey} style={{ marginBottom: '1.1rem' }}>
                      <label style={{ display: 'block', fontFamily: "'Oswald', sans-serif", fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#ffffff', marginBottom: '0.4rem' }}>{t(labelKey)}</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={isPassword && showPasswords ? 'text' : type}
                          placeholder={t(placeholderKey)}
                          value={value}
                          onChange={e => setter(e.target.value)}
                          required
                          className="barber-input login-input"
                          style={{ color: '#ffffff', paddingRight: isPassword ? '2.8rem' : undefined }}
                        />
                        {isPassword && (
                          <button
                            type="button"
                            onClick={() => setShowPasswords(prev => !prev)}
                            aria-label={showPasswords ? t('auth.hidePassword', 'Hide password') : t('auth.showPassword', 'Show password')}
                            style={{ position: 'absolute', right: '0.8rem', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', color: '#ffffff', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          >
                            <PasswordVisibilityIcon visible={showPasswords} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                /* Phone Specific Fields */
                <>
                  <div style={{ marginBottom: '1.1rem' }}>
                    <label style={{ display: 'block', fontFamily: "'Oswald', sans-serif", fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#ffffff', marginBottom: '0.4rem' }}>Phone Number</label>
                    <input
                      type="tel"
                      placeholder="+1234567890"
                      value={phoneNumber}
                      onChange={e => setPhoneNumber(e.target.value)}
                      required
                      disabled={otpRequested}
                      className="barber-input login-input"
                      style={{ color: '#ffffff' }}
                    />
                  </div>

                  {otpRequested && (
                    <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.9)', marginBottom: '1.25rem', lineHeight: 1.5 }}>
                      {t('auth.verificationLinkSent')}
                    </p>
                  )}
                </>
              )}

              {/* Verification method (below confirm password / verification code) */}
              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ fontFamily: "'Oswald', sans-serif", fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#ffffff', marginBottom: '0.75rem' }}>{t('auth.verificationVia')}</p>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                  <button
                    type="button"
                    onClick={() => { setLoginMethod('email'); setError(''); }}
                    style={{ flex: 1, padding: '0.5rem', background: loginMethod === 'email' ? accent : 'transparent', color: loginMethod === 'email' ? '#fff' : '#ffffff', border: `1px solid ${accentBorder}`, borderRadius: '6px', cursor: 'pointer', fontFamily: "'Oswald', sans-serif" }}
                  >
                    EMAIL
                  </button>
                  <button
                    type="button"
                    onClick={() => { setLoginMethod('phone'); setError(''); setOtpRequested(false); }}
                    style={{ flex: 1, padding: '0.5rem', background: loginMethod === 'phone' ? accent : 'transparent', color: loginMethod === 'phone' ? '#fff' : '#ffffff', border: `1px solid ${accentBorder}`, borderRadius: '6px', cursor: 'pointer', fontFamily: "'Oswald', sans-serif" }}
                  >
                    PHONE (OTP)
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-orange"
                style={{ width: '100%', justifyContent: 'center', fontSize: '0.95rem', padding: '0.9rem', marginTop: '0.75rem', opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
              >
                {loading ? t('auth.creatingAccount') : (loginMethod === 'phone' ? (otpRequested ? t('auth.resendLink') : t('auth.sendVerificationLink')) : t('auth.createAccountBtn'))}
              </button>

              <div style={{ marginTop: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ flex: 1, height: 1, background: accentBorder }} />
                <span style={{ fontSize: '0.75rem', color: '#ffffff', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('auth.or', 'or')}</span>
                <div style={{ flex: 1, height: 1, background: accentBorder }} />
              </div>
              <button
                type="button"
                onClick={() => { window.location.href = api.getGoogleAuthUrl(); }}
                style={{ width: '100%', marginTop: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.75rem 1rem', background: '#fff', color: '#333', border: `1px solid ${accentBorder}`, borderRadius: '8px', cursor: 'pointer', fontFamily: "'Oswald', sans-serif", fontSize: '0.9rem' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t('auth.signUpWithGoogle', 'Sign up with Google')}
              </button>
            </form>
          )}
        </div>

        <p style={{ textAlign: 'center', fontSize: '0.85rem', marginTop: '1.5rem' }}>
          <span style={{ color: '#ffffff' }}>{t('auth.alreadyHaveAccountQuestion')}{' '}</span>
          <Link to="/login" style={{ color: '#ffffff', textDecoration: 'none', fontWeight: 600, fontFamily: "'Oswald', sans-serif" }}>
            {t('auth.signInArrow')}
          </Link>
        </p>
      </div>
    </div>
  );
}
